/**
 * General Process (obj_type "G") model types.
 *
 * Mirrors the backend's process shape (app/services/genprocess) — itself a
 * twasta-adapted port of the standalone gen-process project. Databases are
 * referenced, never embedded: an empty connection id means the PROJECT
 * database; a non-empty id names an external connection from the project's
 * encrypted registry (the same records visuals target).
 */

// item_change + validation rule shapes are SHARED with the transaction
// designer (reused verbatim, incl. its editors) — see @twasta/page-runtime.
import type { ItemChange, Validation } from '@/pages/designers/transaction/types'
export type { ItemChange, Validation, ValidationRule } from '@/pages/designers/transaction/types'

export type CalcType = 'E' | 'C' | 'S' | 'L'

export interface Calculation {
  col_name: string
  calc_type: CalcType
  calc_expression: string
  /** Optional display heading for the produced column. */
  header?: string
}

export type ColumnType =
  | 'string' | 'text' | 'int' | 'bigint' | 'decimal' | 'float'
  | 'boolean' | 'date' | 'datetime'

// A reference-table lookup attached to a column (or criterion). When the column
// is editable, the Review grid offers a 🔍 picker that searches this table and
// stores the chosen ``value_column`` value into the cell. The table is read from
// the process's TARGET database (project DB by default).
export interface ColumnLookup {
  table: string             // reference table in the TARGET database
  value_column: string      // column whose value is stored into the cell
  display_column?: string   // column shown to the user (defaults to value_column)
  // Extra columns to match the search text against (display + value always searched).
  search_columns?: string[]
  // Optional SQL WHERE-fragment applied to the reference table. May reference the
  // current row's columns with {col} placeholders; each {col} expands to a typed,
  // escaped SQL literal (do NOT wrap the placeholder in quotes yourself).
  filter?: string
}

// One display/value row returned by the inline lookup search.
export interface ColumnLookupRow {
  display: string
  value: unknown
}

export interface TargetColumn {
  name: string        // column name in target table ('' = unmapped/carried)
  type: ColumnType
  nullable?: boolean
  source?: string     // source row-set column to map from; defaults to name
  // Optional reference-table lookup for inline editing on the Review grid.
  lookup?: ColumnLookup
}

// ── saved row filters (Review grid) ────────────────────────────────────────
// A process can carry any number of named filter expressions, each built in the
// filter form (builder conditions) or typed raw, independently enabled/disabled.
// Enabled filters combine with AND on the preview. Persisted under ``filters``.
export type FilterMode = 'builder' | 'expression'
export type FilterCombinator = 'and' | 'or'

export interface FilterConditionModel {
  id: string
  column: string
  op: string
  value: string    // single value, comma list (for `in`), or lower bound (`between`)
  value2: string   // upper bound (for `between`)
}

export interface SavedFilter {
  name: string
  enabled: boolean
  // Compiled expression string actually evaluated against each row.
  expression: string
  // Builder state, retained so re-opening the form restores the conditions.
  mode?: FilterMode
  conditions?: FilterConditionModel[]
  combinator?: FilterCombinator
}

// ── transaction persist mode ───────────────────────────────────────────────

export interface TxnFieldMap {
  /** Transaction form column (its form key — normalised name). */
  target: string
  /** Source row-set column (lower-cased). */
  source: string
}

export interface TransactionTarget {
  obj_name: string
  /** Source columns identifying ONE transaction (the unique txn id).
      Rows are grouped on these; each group saves as one header+details. */
  group_by: string[]
  header_fields: TxnFieldMap[]
  /** form_no → field maps. Each row of a group becomes one row per form. */
  detail_fields: Record<string, TxnFieldMap[]>
  /** Run insert-time item_change seeding on generated rows (engine option). */
  apply_item_change?: boolean
}

// ── lookups (GENLOOKUP definitions embedded in the process) ───────────────

export type LookupRangeType = 'F' | 'S'   // Fixed / Slab
export type LookupDType = 'N' | 'D' | 'S' // Numeric / Date / String

export interface LookupRow {
  result_value: string
  min_key_value: string
  max_key_value?: string | null
  eff_from?: string | null
  valid_upto?: string | null
}

export interface LookupDefinition {
  lookup_table: string
  lookup_type: LookupRangeType
  key_data_type: LookupDType
  result_data_type: LookupDType
  descr?: string
  sh_descr?: string
  lookup_use?: string
  rows: LookupRow[]
}

export interface RequiredLookupTable {
  lookup_table: string
  source: 'literal' | 'dynamic' | 'embedded'
  dynamic_column?: string | null
  calc_col?: string | null
  lookup_use?: string
  definition: LookupDefinition
}

// ── the process ────────────────────────────────────────────────────────────

/** Where the result rows go. `business_logic` means the process's own
    function does the writing — there is no target table or transaction.
    `none` is a PROVIDER: the process only previews, its rows feeding another
    form's lines (a line source) — it has no Execute and never writes. */
export type PersistMode = 'table' | 'transaction' | 'business_logic' | 'none'

// How Execute behaves on the Review surface. One mutually-exclusive choice that
// supersedes the older `MultiRowSelect` / `columnEditing` booleans (still
// written for the engine + back-compat):
//   direct  — run the source SQL and write everything, NO data review/preview
//   preview — show the rows for review, Execute writes them all
//   select  — show the rows, the user ticks which ones Execute writes
//   edit    — show the rows, the user may edit cell values before Execute
export type ExecutionMode = 'direct' | 'preview' | 'select' | 'edit'

/** Derive the execution mode from a process. Falls back to the legacy
    booleans for processes saved before `execution_mode` existed. */
export function executionModeOf(p: GenProcess): ExecutionMode {
  if (p.execution_mode) return p.execution_mode
  if (p.columnEditing) return 'edit'
  if (p.MultiRowSelect) return 'select'
  return 'preview'
}

/** The model patch that selects an execution mode — sets `execution_mode` and
    keeps the legacy `MultiRowSelect` / `columnEditing` flags in lock-step so the
    engine and every existing read path behave correctly. */
export function executionModePatch(
  p: GenProcess, mode: ExecutionMode,
): Partial<GenProcess> {
  const patch: Partial<GenProcess> = {
    execution_mode: mode,
    // 'edit' also uses row selection — an edited row auto-selects and Execute
    // writes only selected rows — so both 'select' and 'edit' set MultiRowSelect.
    MultiRowSelect: mode === 'select' || mode === 'edit',
    columnEditing: mode === 'edit',
  }
  // Seed editable-columns scope the first time edit mode is chosen.
  if (mode === 'edit' && !p.editableColumns) patch.editableColumns = 'all'
  return patch
}

/** Where a process's rows come from: a SELECT, or an imported file. */
export type SourceKind = 'sql' | 'file'

/** One column of an imported file, as DECLARED at design time. The
    declaration — not the uploaded file — decides the shape of every run. */
export interface FileSourceColumn {
  name: string
  /** Original file header, kept so the mapping stays readable. */
  label?: string
  type: ColumnType
  required?: boolean
}

/** The file-import declaration (`source_kind: 'file'`).

    The file is parsed in the browser and its rows are sent with each run as
    `input_rows`; nothing is uploaded or stored server-side. `sample_rows`
    holds a handful of rows captured when the columns were declared, so the
    calculations and target tabs stay workable without re-importing. */
export interface FileSource {
  columns: FileSourceColumn[]
  has_header?: boolean
  delimiter?: string
  /** Lower-cased file header → declared column name. */
  mapping?: Record<string, string>
  sample_rows?: Record<string, unknown>[]
}

export interface GenProcess {
  obj_name?: string
  name: string
  description?: string
  /** '' → project database (default). Else a CONNECTION_ID. */
  source_connection_id?: string
  /** 'sql' (default) or 'file' — an imported CSV instead of a query. */
  source_kind?: SourceKind
  /** Column + mapping declaration when `source_kind` is 'file'. */
  file_source?: FileSource
  source_sql: string
  default_params?: Record<string, string>
  calculations: Calculation[]
  lookups?: LookupDefinition[]
  persist_mode?: PersistMode
  /** Table mode — '' → project database (default). Else a CONNECTION_ID. */
  target_connection_id?: string
  target_table?: string
  target_columns?: TargetColumn[]
  /** Storage format on an ANALYTICS target (Dremio): '' → the connection's
      default ('iceberg'). 'parquet' / 'csv' are files, rewritten whole on
      every run — they cannot use key columns. Ignored on ordinary
      databases, which always write a native table. */
  target_format?: string
  key_columns?: string[]
  /** Transaction mode mapping. */
  transaction_target?: TransactionTarget
  /** When this process is used as a LINE SOURCE, the transaction object whose
      row security scopes its rows for the caller (its output must carry the
      scoped columns). Absent → in transaction mode the target transaction;
      otherwise the source SQL alone decides. */
  row_security_from?: { object: string; form_no?: string }
  /** PROCESS-level business logic — one function called ONCE with every row
      about to be persisted. In `table` / `transaction` mode it runs before the
      write (and may return a replacement row set); in `business_logic` mode it
      IS the write. Same shape as a transaction's pre-commit hook, so the same
      editor edits it. */
  business_logic?: ItemChange
  /** Review-grid behaviour. `execution_mode` is the canonical selector; the two
      booleans below are kept in sync for the engine + legacy read paths. */
  execution_mode?: ExecutionMode
  MultiRowSelect?: boolean
  columnEditing?: boolean
  editableColumns?: 'all' | string[]
  /** Reference-table lookups for editable columns, keyed by the lower-cased
      column name. Used for columns NOT in target_columns (e.g. transaction
      persist mode, or raw source columns) — the table-mode grid stores its
      lookup on the TargetColumn instead. Resolved by the engine's lookup
      search after target_columns. */
  editable_lookups?: Record<string, ColumnLookup>
  /** item_change rules keyed by the lower-cased editable column that triggers
      them: when that column's value changes, set/lock OTHER columns in the row
      (Rule / Business Logic / Call API). Same shape as the transaction designer. */
  item_changes?: Record<string, ItemChange>
  /** Structured validation rules run over the result rows on Execute
      (built-in / expression / business_logic / rest_api, severity E/W/P).
      A `Validation` is a ValidationRule object or a legacy call-string. */
  validations?: Validation[]
  /** Custom display headings for SOURCE columns, keyed by field name. */
  column_headers?: Record<string, string>
  /** Persisted Review-grid column widths, keyed by field name (drag-resized). */
  column_widths?: Record<string, number>
  /** Named, independently enable-able row filters for the Review preview. */
  filters?: SavedFilter[]
  /** Reference-table lookups attached to criteria (the `?.NAME` prompts),
      keyed by the lower-cased criterion name — shows a 🔍 picker per criterion. */
  criteria_lookups?: Record<string, ColumnLookup>
  /** Per-criterion presentation: label / order / hidden / read-only / default
      (authored, or customized per organization by its functional admin). */
  criteria_settings?: Record<string, {
    label?: string; order?: number | string; hidden?: boolean
    protect?: boolean; default?: unknown
  }>
  /** Per-column presentation on the result grid: order / hidden / read-only. */
  column_settings?: Record<string, { order?: number | string; hidden?: boolean; protect?: boolean }>
  /** Caption per criterion (the `?.NAME` prompts), keyed by criterion name —
      overrides the label humanised from the name (e.g. a reverse-imported
      legacy process screen's own field captions). */
  criteria_labels?: Record<string, string>
}

// ── runtime responses ──────────────────────────────────────────────────────

export interface CellTrace {
  // 'R' is a synthetic trace built client-side when drilling into a raw source
  // column (one that didn't come from a calculation). The backend never emits 'R'.
  calc_type: 'E' | 'C' | 'S' | 'L' | 'R'
  title?: string
  expression: string
  result: unknown
  rendered?: string
  inputs?: Record<string, unknown>
  condition?: string
  true_expression?: string
  false_expression?: string
  condition_result?: boolean | null
  branch_taken?: 'true' | 'false'
  branch_expression?: string
  lookup_table?: string
  key_column?: string
  date_column?: string | null
  key_value?: unknown
  date_value?: unknown
  lookup_type?: 'Slab' | 'Fixed'
  key_data_type?: string
  result_data_type?: string
  candidates?: {
    result_value: unknown
    min_key_value: unknown
    max_key_value: unknown
    eff_from?: string | null
    valid_upto?: string | null
    matched: boolean
    reason: string
  }[]
  matched_index?: number
  why?: string
  error?: string
}

export interface PreviewResponse {
  columns: string[]
  rows: Record<string, unknown>[]
  row_count: number
  source_sql_executed: string
  calc_columns?: string[]
  traces?: Record<string, Record<string, CellTrace>>
}

export interface PersistResponse {
  // 'merge' / 'replace' come from an ANALYTICS target (see target_format):
  // Dremio reports one affected count for a MERGE, so those report `written`
  // instead of an inserted/updated split.
  mode: 'insert' | 'upsert' | 'transaction' | 'merge' | 'replace' | 'business_logic'
  // table modes
  inserted?: number
  updated?: number
  written?: number
  target_format?: string
  warning?: string
  target_table?: string
  table_created?: boolean
  key_columns?: string[]
  audit_columns_added?: string[]
  target_database?: string
  // transaction mode
  target_object?: string
  transactions?: number
  rows?: number
  ids?: (number | string | null)[]
  groups?: { group: string; txn_id?: string | null; detail_rows?: Record<string, number> }[]
  warnings?: string[]
  // business_logic mode
  method?: string
  /** The sentence the process's business logic reported when it finished —
      shown as the run's result whichever persist mode produced it. */
  message?: string
}

export interface CriterionItem {
  name: string
  data_type: 'string' | 'date' | 'number'
  operator: string
  multi: 'single' | 'list' | 'range'
  label: string
  default?: unknown
  default_helper?: string
  column_hint?: string
  value?: unknown
}

// ── transaction catalogue (mapping UI) ─────────────────────────────────────

export interface TxnFormColumn {
  form_key: string
  name: string
  heading: string
  db_name: string
  col_type: string
  key: boolean
  mandatory: boolean
  auto: boolean
}

export interface TxnForm {
  form_no: string
  form_title: string
  parent_form_no: string | null
  is_header: boolean
  columns: TxnFormColumn[]
}

export interface TxnCatalogEntry {
  obj_name: string
  forms: TxnForm[]
}

export interface ExternalConnection {
  CONNECTION_ID: string
  CONNECTION_NAME?: string
  DATABASE_TYPE?: string
  HOST?: string
  DATABASE?: string
}

/** What a DATABASE_TYPE accepts as a persist target, from
    `GET /connections/types` (null for every ordinary database, which just
    writes a native table). Drives the Target tab's storage-format picker. */
export interface WriteCapability {
  formats: string[]
  merge: boolean
  identity_pk: boolean
}

export interface ConnectionTypeInfo {
  value: string
  label: string
  warehouse?: boolean
  writeback?: WriteCapability | null
}

export function blankProcess(objName: string): GenProcess {
  return {
    obj_name: objName,
    name: objName,
    description: '',
    source_connection_id: '',
    source_kind: 'sql',
    source_sql: '',
    default_params: {},
    calculations: [],
    lookups: [],
    persist_mode: 'table',
    target_connection_id: '',
    target_table: '',
    target_columns: [],
    target_format: '',
    key_columns: [],
    MultiRowSelect: false,
    columnEditing: false,
  }
}

/** Accept agent-authored shapes: a {"process": {...}} wrapper and missing
    arrays. Returns a normalised copy safe for the designer to edit. */
export function sanitizeProcess(raw: unknown, objName: string): GenProcess {
  let p = (raw ?? {}) as Record<string, unknown>
  if (p.process && typeof p.process === 'object') p = p.process as Record<string, unknown>
  const base = blankProcess(objName)
  const out: GenProcess = {
    ...base,
    ...p,
    obj_name: objName,
    name: typeof p.name === 'string' && p.name ? p.name : objName,
    calculations: Array.isArray(p.calculations) ? (p.calculations as Calculation[]) : [],
    lookups: Array.isArray(p.lookups) ? (p.lookups as LookupDefinition[]) : [],
    target_columns: Array.isArray(p.target_columns) ? (p.target_columns as TargetColumn[]) : [],
    key_columns: Array.isArray(p.key_columns) ? (p.key_columns as string[]) : [],
    default_params: (p.default_params && typeof p.default_params === 'object')
      ? (p.default_params as Record<string, string>) : {},
  }
  if (out.persist_mode !== 'transaction' && out.persist_mode !== 'business_logic'
      && out.persist_mode !== 'none') {
    out.persist_mode = 'table'
  }
  // Keep a business-logic hook only when it is a real declaration.
  const bl = out.business_logic as ItemChange | undefined
  if (!bl || typeof bl !== 'object' || !bl.mode) delete out.business_logic
  out.source_kind = out.source_kind === 'file' ? 'file' : 'sql'
  if (out.source_kind === 'file') {
    const fs = (out.file_source ?? {}) as FileSource
    out.file_source = {
      columns: Array.isArray(fs.columns) ? fs.columns : [],
      has_header: fs.has_header !== false,
      delimiter: typeof fs.delimiter === 'string' ? fs.delimiter : ',',
      mapping: (fs.mapping && typeof fs.mapping === 'object') ? fs.mapping : {},
      sample_rows: Array.isArray(fs.sample_rows) ? fs.sample_rows : [],
    }
  } else {
    delete out.file_source
  }
  // Normalise the execution mode (deriving it from the legacy booleans for
  // processes saved before it existed) and re-sync the booleans to it.
  const mode = executionModeOf(out)
  out.execution_mode = mode
  out.MultiRowSelect = mode === 'select' || mode === 'edit'
  out.columnEditing = mode === 'edit'
  if (out.transaction_target) {
    const tt = out.transaction_target as TransactionTarget
    out.transaction_target = {
      obj_name: tt.obj_name || '',
      group_by: Array.isArray(tt.group_by) ? tt.group_by : [],
      header_fields: Array.isArray(tt.header_fields) ? tt.header_fields : [],
      detail_fields: (tt.detail_fields && typeof tt.detail_fields === 'object')
        ? tt.detail_fields : {},
      apply_item_change: !!tt.apply_item_change,
    }
  }
  return out
}
